import { SortDir, SortKey, defaultDir } from './browseQuery';
import { single, list } from './queryParams';

//  Aliased rather than redeclared: the three sort keys are identical on both
//  browse pages, so a second copy would only invite drift.
export type SkillSortKey = SortKey;

export interface ISkillBrowseFilter {
    name: string;
    //  Every TICKED job id, not just the clicked node. The class selector
    //  expands a click to the node's subtree, which is what lets the chip row
    //  collapse a whole branch into one chip. Ancestors are added later, at
    //  request time, and only when inheritJobs is on.
    jobs: number[];
    inheritJobs: boolean;
    skillTypes: number[];
    durationTypes: number[];
    targetTypes: number[];
    //  -1 (No element) and -1 (No weapon) are legal values in the data, not
    //  sentinels for "unset" -- an empty array is what "unset" means here.
    elements: number[];
    weapons: number[];
    groups: number[];
    skillGroups: number[];
    globalSkillGroups: number[];
    sort: SkillSortKey;
    dir: SortDir;
}

export function emptySkillFilter(): ISkillBrowseFilter {
    return {
        name: '',
        jobs: [], inheritJobs: true,
        skillTypes: [], durationTypes: [], targetTypes: [],
        elements: [], weapons: [],
        groups: [], skillGroups: [], globalSkillGroups: [],
        sort: 'relevance', dir: 'desc',
    };
}

//  Whether any PROPERTY filter is set. Deliberately ignores name/sort/dir:
//  /skills/search accepts a filter-only query with no name, and the page's
//  prompt gate is `name is long enough OR a filter is active`.
export function isSkillFilterActive(f: ISkillBrowseFilter): boolean {
    return f.jobs.length > 0
        || f.skillTypes.length > 0
        || f.durationTypes.length > 0
        || f.targetTypes.length > 0
        || f.elements.length > 0
        || f.weapons.length > 0
        || f.groups.length > 0
        || f.skillGroups.length > 0
        || f.globalSkillGroups.length > 0;
}

export function skillFilterToQuery(f: ISkillBrowseFilter): Record<string, string> {
    const q: Record<string, string> = {};
    //  `q`, not `name`: /skills?q=... links already exist in the wild, written
    //  by the page this replaces and linked from QuickNav.
    if (f.name) q.q = f.name;
    if (f.jobs.length) q.job = f.jobs.join(',');
    //  Written only when off, since inherit-on is the default.
    if (!f.inheritJobs) q.inh = '0';
    if (f.skillTypes.length) q.st = f.skillTypes.join(',');
    if (f.durationTypes.length) q.dt = f.durationTypes.join(',');
    if (f.targetTypes.length) q.tt = f.targetTypes.join(',');
    if (f.elements.length) q.el = f.elements.join(',');
    if (f.weapons.length) q.wp = f.weapons.join(',');
    if (f.groups.length) q.grp = f.groups.join(',');
    if (f.skillGroups.length) q.sg = f.skillGroups.join(',');
    if (f.globalSkillGroups.length) q.gsg = f.globalSkillGroups.join(',');
    if (f.sort !== 'relevance') q.sort = f.sort;
    if (f.dir !== defaultDir(f.sort)) q.dir = f.dir;
    return q;
}

export function queryToSkillFilter(
    q: Record<string, string | string[] | undefined>,
): ISkillBrowseFilter {
    const sortRaw = single(q.sort);
    const sort: SkillSortKey = sortRaw === 'name' || sortRaw === 'id' ? sortRaw : 'relevance';
    const dirRaw = single(q.dir);
    const dir: SortDir = dirRaw === 'asc' || dirRaw === 'desc' ? dirRaw : defaultDir(sort);
    return {
        name: single(q.q) ?? '',
        jobs: list(q.job),
        //  Only an explicit '0' turns inherit off. An absent key is the
        //  default, and a garbage value must not silently narrow a search.
        inheritJobs: single(q.inh) !== '0',
        skillTypes: list(q.st),
        durationTypes: list(q.dt),
        targetTypes: list(q.tt),
        elements: list(q.el),
        weapons: list(q.wp),
        groups: list(q.grp),
        skillGroups: list(q.sg),
        globalSkillGroups: list(q.gsg),
        sort, dir,
    };
}
